import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { Button, Typography, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';

import { Wrapper, Container } from 'containers/AccessCode/styled';
import { AccessCodeForm } from 'components/DeliveryManager/AccessCodeForm';
import Footer from 'components/Footer/FooterContainer';
import { create } from 'apisauce';
import { MANAGE_DELIVERY_LITE_DEFAULT_TEXT } from 'constants/verbiage';

const PREFIX = 'ManageDeliveryLite';

const classes = {
  root: `${PREFIX}-root`,
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  section: `${PREFIX}-section`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  submit: `${PREFIX}-submit`,
  loading: `${PREFIX}-loading`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {
    borderRadius: '2rem',
    fontWeight: 700,
    borderWidth: '2px !important',
    borderColor: theme.palette.primary.main,
  },

  [`& .${classes.container}`]: {
    color: theme.palette.common12,
    padding: '1rem 1.5rem',
    [theme.breakpoints.up('sm')]: {
      padding: '2rem 3rem',
    },
    border: `0.3px solid ${theme.colorTokens.outlineVariant}`,
    backgroundColor: theme.colorTokens.surfaceContainerLowest,
    borderRadius: '8px',
    marginTop: '2rem',
  },

  [`& .${classes.header}`]: {
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: `1px solid ${theme.colorTokens.outlineVariant}`,
  },

  [`& .${classes.title}`]: {
    fontSize: '24px',
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: '0.5rem',
  },

  [`& .${classes.subtitle}`]: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
  },

  [`& .${classes.section}`]: {
    marginBottom: '1.5rem',
  },

  [`& .${classes.sectionTitle}`]: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '1rem',
    color: theme.palette.text.primary,
  },

  [`& .${classes.submit}`]: {
    marginTop: '2rem',
  },

  [`& .${classes.loading}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
}));

export const ManageDeliveryLite = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const navigate = useNavigate();
  const { trackingCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [accessCodeTypes, setAccessCodeTypes] = useState({});
  const timerRef = React.useRef(null);

  const { handleSubmit, control, setValue, formState, watch } = useForm({
    defaultValues: { access_code_map: [] },
    shouldFocusError: true,
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'access_code_map' });
  const { errors } = formState;

  const watchAccessCodes = watch('access_code_map');
  const controlledAccessCodes = fields.map((field, index) => ({ ...field, ...watchAccessCodes[index] }));

  // Create API instance without authentication (memoized to prevent infinite loop)
  const api = useMemo(
    () =>
      create({
        baseURL: import.meta.env.REACT_APP_API_ROOT,
        withCredentials: true,
      }),
    [],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch delivery data without authentication
      const deliveryResponse = await api.get(`delivery/${trackingCode}`);
      if (deliveryResponse.status !== 200) {
        toast.error('Unable to load delivery information', { theme: 'colored', hideProgressBar: true });
        return;
      }

      setDelivery(deliveryResponse.data);

      // Check if the feature is enabled for this client
      const settings = deliveryResponse.data?.settings || {};
      const isEnabled = settings.enable_manage_delivery_lite === true || settings.enable_manage_delivery_lite === 'true';

      if (!isEnabled) {
        // Feature is disabled, redirect to tracking page
        toast.error('This feature is not available', { theme: 'colored', hideProgressBar: true });
        navigate(`/${trackingCode}`);
        return;
      }

      // Extract access code types from settings
      // Settings contain keys like "access_codes/BUILDING_CODE": "Building Code"
      const types = {};
      Object.keys(settings).forEach((key) => {
        if (key.startsWith('access_codes/')) {
          const typeKey = key.replace('access_codes/', '');
          types[typeKey] = settings[key];
        }
      });
      setAccessCodeTypes(types);

      // Pre-populate existing access codes if any
      const existingCodes = deliveryResponse.data?.access_code_map || {};
      const codes = Object.keys(existingCodes)
        .map((type) => ({ type, code: existingCodes[type] }))
        .filter(({ code }) => Boolean(code));

      if (codes.length > 0) {
        setValue('access_code_map', codes);
      }
    } catch (error) {
      toast.error('Unable to load delivery information', { theme: 'colored', hideProgressBar: true });
    } finally {
      setLoading(false);
    }
  }, [api, trackingCode, setValue, navigate]);

  useEffect(() => {
    fetchData();

    return () => {
      // Clear any pending redirect timer on unmount
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [fetchData]);

  const onSubmit = async (data) => {
    if (!delivery?.shipment?.id) {
      toast.error('Unable to submit. Please try again.', { theme: 'colored', hideProgressBar: true });
      return;
    }

    try {
      setSubmitting(true);
      // no-dd-sa:javascript-code-style/assignment-name
      const access_codes = data.access_code_map;
      // no-dd-sa:javascript-code-style/assignment-name
      const access_code_map = access_codes.reduce((obj, item) => {
        obj[item.type] = item.code;
        return obj;
      }, {});

      // Use the new unauthenticated endpoint for access codes
      const response = await api.post(`delivery/${trackingCode}/${delivery.shipment.id}/update-access-codes`, access_code_map, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        toast.success('Access code saved successfully!', { theme: 'colored', hideProgressBar: true });
        // Clear any existing timer before setting a new one
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        // Store timer ID so it can be cleaned up on unmount
        timerRef.current = setTimeout(() => {
          navigate(`/${trackingCode}`);
        }, 1500);
      } else {
        toast.error('Something went wrong. Please try again!', { theme: 'colored', hideProgressBar: true });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again!', { theme: 'colored', hideProgressBar: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <Container>
          <div className={classes.loading}>
            <CircularProgress color="primary" />
          </div>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Root>
      <Wrapper>
        <Container>
          <div className={classes.container}>
            <div className={classes.header}>
              <Typography className={classes.title}>Add Access Code</Typography>
              <Typography className={classes.subtitle}>
                {delivery?.settings?.manage_delivery_lite_text ||
                  store.deliveryStore.settings?.manage_delivery_lite_text ||
                  MANAGE_DELIVERY_LITE_DEFAULT_TEXT}
              </Typography>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>Access Codes:</Typography>
                <AccessCodeForm
                  fields={fields}
                  append={append}
                  remove={remove}
                  control={control}
                  controlledAccessCodes={controlledAccessCodes}
                  errors={errors}
                  accessCodeTypes={accessCodeTypes}
                />
              </div>

              <div className={classes.submit}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  classes={{ root: classes.root }}
                  disableElevation
                  disabled={submitting || fields.length === 0}
                >
                  {submitting ? 'Saving...' : 'Save Access Code'}
                </Button>
              </div>
            </form>
          </div>
        </Container>
      </Wrapper>
      <Footer />
    </Root>
  );
});

export default ManageDeliveryLite;
